import { ArrowRight, Info } from 'lucide-react';
import { useQueryState } from 'nuqs';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useProjectsContext } from 'src/modules/projects/context';
import { useTiersQuery } from 'src/modules/tiers/hooks/useTiersQuery';

import { Callout } from '@/components/callouts/Callout';
import { SimpleModal } from '@/components/modal';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  TextArea,
  Tooltip,
} from '@/design-system/components/ui';

import { AffiliateApplicationStatus, ApproveAffiliateApplicationDto } from '../domain/affiliate-application-types';
import { useAffiliateApplicationFormConfig } from '../hooks/useAffiliateApplicationFormConfig';
import { useAffiliateApplications } from '../hooks/useAffiliateApplications';
import { useApproveAffiliateApplication } from '../hooks/useApproveAffiliateApplication';
import { useHoldAffiliateApplication } from '../hooks/useHoldAffiliateApplication';
import { useRejectAffiliateApplication } from '../hooks/useRejectAffiliateApplication';
import { useUpdateAffiliateApplication } from '../hooks/useUpdateAffiliateApplication';
import { AffiliateApplicationsBottomBar } from './AffiliateApplicationsBottomBar';
import { AffiliateApplicationsEmptyState } from './AffiliateApplicationsEmptyState';
import { AffiliateApplicationsTable } from './AffiliateApplicationsTable';
import { AffiliateApplicationsTotalsCards } from './AffiliateApplicationsTotalsCards';

export const dateFormat = 'MMM D, YYYY HH:mm:ss';

interface AffiliateApplicationsViewProps {
  slug: string;
}

export const AffiliateApplicationsView = ({ slug }: AffiliateApplicationsViewProps) => {
  const { project } = useProjectsContext();
  const [initialLoading, setInitialLoading] = useState(true);

  const [status] = useQueryState('status');
  const [userIdentifier] = useQueryState('user_identifier');
  const [page, setPage] = useQueryState('page');
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState<'created_at' | 'updated_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectAdminNotes, setRejectAdminNotes] = useState('');
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdAdminNotes, setHoldAdminNotes] = useState('');
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveAdminNotes, setApproveAdminNotes] = useState('');
  const [selectedTierId, setSelectedTierId] = useState('');
  const [protectionDays, setProtectionDays] = useState<string>('');
  const [adminNotesModalOpen, setAdminNotesModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const { applications, pagination, isLoading, isError, refetch, isRefetching } = useAffiliateApplications({
    projectId: project?.id || '',
    status: status as AffiliateApplicationStatus | undefined,
    page: page ? parseInt(page) : 1,
    page_size: pageSize,
    sort_by: sortBy || 'created_at',
    sort_order: sortOrder || 'desc',
    search: userIdentifier || undefined,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage.toString());
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage('1');
  };

  const { pagination: totalPagination, isLoading: totalLoading } = useAffiliateApplications({
    projectId: project?.id || '',
    page_size: 1,
  });

  const { pagination: pendingPagination, isLoading: pendingLoading } = useAffiliateApplications({
    projectId: project?.id || '',
    status: AffiliateApplicationStatus.Pending,
    page_size: 1,
  });

  const { pagination: approvedPagination, isLoading: approvedLoading } = useAffiliateApplications({
    projectId: project?.id || '',
    status: AffiliateApplicationStatus.Approved,
    page_size: 1,
  });

  const { pagination: rejectedPagination, isLoading: rejectedLoading } = useAffiliateApplications({
    projectId: project?.id || '',
    status: AffiliateApplicationStatus.Rejected,
    page_size: 1,
  });

  const { pagination: onHoldPagination, isLoading: onHoldLoading } = useAffiliateApplications({
    projectId: project?.id || '',
    status: AffiliateApplicationStatus.OnHold,
    page_size: 1,
  });

  const {
    formConfig,
    isLoading: isFormConfigLoading,
    isNotFound,
  } = useAffiliateApplicationFormConfig({
    projectId: project?.id || '',
  });

  const { approveApplication, isLoading: isApproving } = useApproveAffiliateApplication();
  const { rejectApplication, isLoading: isRejecting } = useRejectAffiliateApplication();
  const { holdApplication, isLoading: isHolding } = useHoldAffiliateApplication();
  const { updateApplication, isLoading: isUpdating } = useUpdateAffiliateApplication();

  const { data: tiers = [] } = useTiersQuery();

  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => a.rank - b.rank), [tiers]);

  useEffect(() => {
    if (!isLoading && initialLoading) {
      setInitialLoading(false);
    }
  }, [isLoading, initialLoading]);

  const statusCounts = useMemo(() => {
    return {
      total: totalPagination.total,
      pending: pendingPagination.total,
      approved: approvedPagination.total,
      rejected: rejectedPagination.total,
      on_hold: onHoldPagination.total,
    };
  }, [
    totalPagination.total,
    pendingPagination.total,
    approvedPagination.total,
    rejectedPagination.total,
    onHoldPagination.total,
  ]);

  const totalsLoading = totalLoading || pendingLoading || approvedLoading || rejectedLoading || onHoldLoading;

  const selectedStatus = useMemo(() => {
    if (selectedRows.length === 0) return null;
    const firstSelectedApp = applications.find((app) => app.id === selectedRows[0]);
    return firstSelectedApp?.status || null;
  }, [selectedRows, applications]);

  const handleRowSelectionChange = (ids: string[]) => {
    setSelectedRows(ids);
  };

  const handleSort = (column: 'created_at' | 'updated_at' | 'status') => {
    setSortBy(column);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleApprove = () => {
    if (selectedRows.length === 0) return;
    setApproveModalOpen(true);
  };

  const handleApproveConfirm = () => {
    if (!selectedStatus) return;

    const parsedDays = parseInt(protectionDays, 10);
    const dto: ApproveAffiliateApplicationDto = {
      tier_id: selectedTierId,
      protection_days: parsedDays,
      ...(approveAdminNotes.trim() ? { admin_notes: approveAdminNotes.trim() } : {}),
    };

    selectedRows.forEach((applicationId) => {
      approveApplication({
        projectId: project?.id || '',
        applicationId,
        sourceStatus: selectedStatus,
        dto,
      });
    });

    setSelectedRows([]);
    setApproveModalOpen(false);
    setApproveAdminNotes('');
    setSelectedTierId('');
    setProtectionDays('');
  };

  const handleReject = () => {
    if (selectedRows.length === 0) return;
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectionReason.trim() || !selectedStatus) {
      toast.error('Please provide a rejection reason');
      return;
    }

    selectedRows.forEach((applicationId) => {
      rejectApplication({
        projectId: project?.id || '',
        applicationId,
        sourceStatus: selectedStatus,
        dto: {
          rejection_reason: rejectionReason,
          ...(rejectAdminNotes.trim() ? { admin_notes: rejectAdminNotes.trim() } : {}),
        },
      });
    });

    setSelectedRows([]);
    setRejectModalOpen(false);
    setRejectionReason('');
    setRejectAdminNotes('');
  };

  const handleHold = () => {
    if (selectedRows.length === 0) return;
    setHoldModalOpen(true);
  };

  const handleHoldConfirm = () => {
    selectedRows.forEach((applicationId) => {
      holdApplication({
        projectId: project?.id || '',
        applicationId,
        ...(holdAdminNotes.trim() ? { dto: { admin_notes: holdAdminNotes.trim() } } : {}),
      });
    });

    setSelectedRows([]);
    setHoldModalOpen(false);
    setHoldAdminNotes('');
  };

  const handleUpdateAdminNotes = () => {
    if (selectedRows.length === 0) return;
    setAdminNotesModalOpen(true);
  };

  const handleUpdateAdminNotesConfirm = () => {
    if (!selectedStatus) return;

    selectedRows.forEach((applicationId) => {
      updateApplication({
        projectId: project?.id || '',
        applicationId,
        currentStatus: selectedStatus,
        dto: {
          admin_notes: adminNotes,
        },
      });
    });

    setSelectedRows([]);
    setAdminNotesModalOpen(false);
    setAdminNotes('');
  };

  if (isNotFound) {
    return <AffiliateApplicationsEmptyState slug={slug} />;
  }

  return (
    <>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:pb-[72px]">
        <div className="tw:flex tw:flex-col tw:gap-6">
          <div>
            {initialLoading ? (
              <div className="tw:flex tw:flex-col tw:gap-4">Loading totals...</div>
            ) : isError ? (
              <Callout title="Something went wrong" type="error" body={'Could not load applications totals'} />
            ) : (
              <AffiliateApplicationsTotalsCards isLoading={totalsLoading} isError={false} statusCounts={statusCounts} />
            )}
          </div>
          <div>
            <AffiliateApplicationsTable
              applications={applications}
              formFields={formConfig?.formFields || []}
              isLoading={initialLoading || isFormConfigLoading}
              isError={isError}
              selectedRows={selectedRows}
              selectedStatus={selectedStatus}
              onRowSelectionChange={handleRowSelectionChange}
              sortBy={sortBy || 'created_at'}
              sortOrder={sortOrder || 'desc'}
              onSort={handleSort}
              onRefresh={refetch}
              isRefetching={isRefetching}
              total={pagination.total}
              page={page ? parseInt(page) : 1}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      <AffiliateApplicationsBottomBar
        selectedCount={selectedRows.length}
        selectedStatus={selectedStatus}
        onApprove={handleApprove}
        onReject={handleReject}
        onHold={handleHold}
        onUpdateAdminNotes={handleUpdateAdminNotes}
        isLoading={isApproving || isRejecting || isHolding || isUpdating}
      />

      <SimpleModal open={rejectModalOpen} title="Reject Applications" onClose={() => setRejectModalOpen(false)}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          <Text variant="body-md">
            Please provide a reason for rejecting {selectedRows.length} application
            {selectedRows.length !== 1 ? 's' : ''}:
          </Text>
          <TextArea
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
          />
          <TextArea
            rows={2}
            label="Admin Notes (optional)"
            value={rejectAdminNotes}
            onChange={(e) => setRejectAdminNotes(e.target.value)}
          />
          <div className="tw:flex tw:flex-row tw:justify-between">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm} disabled={isRejecting}>
              Reject
            </Button>
          </div>
        </div>
      </SimpleModal>

      <SimpleModal open={holdModalOpen} title="Hold Applications" onClose={() => setHoldModalOpen(false)}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          <Text variant="body-md">
            Are you sure you want to hold {selectedRows.length} application
            {selectedRows.length !== 1 ? 's' : ''}?
          </Text>
          <TextArea
            rows={2}
            label="Admin Notes (optional)"
            value={holdAdminNotes}
            onChange={(e) => setHoldAdminNotes(e.target.value)}
          />
          <div className="tw:flex tw:flex-row tw:justify-between">
            <Button variant="outline" onClick={() => setHoldModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleHoldConfirm} disabled={isHolding}>
              Hold
            </Button>
          </div>
        </div>
      </SimpleModal>

      <SimpleModal open={approveModalOpen} title="Approve Applications" onClose={() => setApproveModalOpen(false)}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          <Text variant="body-md">
            Are you sure you want to approve {selectedRows.length} application
            {selectedRows.length !== 1 ? 's' : ''}?
          </Text>
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Text variant="body-sm" weight="semibold">
              Assign to Tier
            </Text>
            <Select value={selectedTierId} onValueChange={setSelectedTierId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a tier" />
              </SelectTrigger>
              <SelectContent>
                {sortedTiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id}>
                    <Text variant="body-md">
                      #{tier.rank} {tier.name}
                    </Text>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tw:flex tw:items-center tw:gap-4">
            <div className="tw:flex tw:flex-col tw:whitespace-nowrap">
              <div className="tw:flex tw:items-center tw:gap-1">
                <Text variant="body-sm" weight="semibold">
                  Protection Period <span className="tw:text-red-500">*</span>
                </Text>
                <Tooltip content="Days that the user will remain in this tier. After that period, they will be automatically assigned to a tier by evaluating the audiences conditions.">
                  <Info className="tw:h-3.5 tw:w-3.5 tw:text-muted-foreground tw:cursor-help" aria-hidden="true" />
                </Tooltip>
              </div>
              <Text variant="body-sm" color="muted">
                Days from affiliate creation
              </Text>
            </div>
            <div className="tw:w-24 tw:shrink-0">
              <Input
                type="number"
                min={1}
                max={365}
                value={protectionDays}
                onChange={(e) => setProtectionDays(e.target.value)}
                placeholder="—"
                style={{ textAlign: 'right' }}
                rightSlot={
                  <Text variant="body-sm" color="muted">
                    days
                  </Text>
                }
              />
            </div>
            {protectionDays && parseInt(protectionDays, 10) >= 1 && (
              <div className="tw:flex tw:flex-col">
                <Text variant="body-sm" color="muted" className="tw:whitespace-nowrap">
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{' '}
                  <ArrowRight className="tw:inline tw:h-3 tw:w-3" aria-hidden="true" />{' '}
                  {(() => {
                    const end = new Date();
                    end.setDate(end.getDate() + parseInt(protectionDays, 10));
                    return end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  })()}
                </Text>
              </div>
            )}
          </div>
          <TextArea
            rows={2}
            label="Admin Notes (optional)"
            value={approveAdminNotes}
            onChange={(e) => setApproveAdminNotes(e.target.value)}
          />
          <div className="tw:flex tw:flex-row tw:justify-between">
            <Button variant="outline" onClick={() => setApproveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApproveConfirm}
              disabled={
                isApproving ||
                !selectedTierId ||
                !protectionDays ||
                parseInt(protectionDays, 10) < 1 ||
                parseInt(protectionDays, 10) > 365
              }
            >
              Approve
            </Button>
          </div>
        </div>
      </SimpleModal>

      <SimpleModal open={adminNotesModalOpen} title="Update Admin Notes" onClose={() => setAdminNotesModalOpen(false)}>
        <div className="tw:flex tw:flex-col tw:gap-6">
          <Text variant="body-md">
            Update admin notes for {selectedRows.length} application
            {selectedRows.length !== 1 ? 's' : ''}:
          </Text>
          <TextArea rows={4} label="Admin Notes" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
          <div className="tw:flex tw:flex-row tw:justify-between">
            <Button variant="outline" onClick={() => setAdminNotesModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdateAdminNotesConfirm} disabled={isUpdating}>
              Update
            </Button>
          </div>
        </div>
      </SimpleModal>
    </>
  );
};
